// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { VariantDisplayTypeValues } from "@insite/client-framework/Types/ApiModels";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import ProductTextBadges, { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import GridContainer, { GridContainerProps } from "@rugby/MobiusOverrides/GridContainer";
import ProductAttributes, { ProductAttributesStyles } from "@rugby/Overrides/Components/ProductAttributes";
import ProductPartNumbers from "@rugby/Overrides/Components/ProductPartNumbers";
import customProductInformationStyles from "@rugby/Overrides/Widgets/ProductList/styles/ProductListProductInformation.style";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    isGridView?: boolean;
    showVariantProps?: boolean;
    showMinPack?: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    container?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    swatchGridItem?: GridItemProps;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    attributesGridItem?: GridItemProps;
    attributes?: ProductAttributesStyles;
    productSearchGridItem?: GridItemProps;
    variantPropsGridItem?: GridItemProps;
    variantWrapper?: InjectableCss;
    variantLabelText?: TypographyPresentationProps;
    variantValueText?: TypographyPresentationProps;
    minPackQuantityGridItem?: GridItemProps;
    minPackQuantityLabelText?: TypographyProps;
    minPackQuantityValueText?: TypographyProps;
    textBadgeStyles?: ProductTextBadgesStyles;
    imageBadgeStyles?: ProductImageBadgesStyles;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    container: {
        gap: 10,
        css: css`
            width: 100%;
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
            `,
            typographyProps: {
                size: 20,
            },
        },
    },
    brandGridItem: {
        width: 12,
    },
    descriptionGridItem: {
        width: 12,
    },
    minPackQuantityGridItem: {
        width: 12,
    },
    partNumbersGridItem: {
        width: 12,
    },
    swatchGridItem: {
        width: 12,
    },
    imageBadgeGridItem: {
        width: 12,
    },
    textBadgeGridItem: {
        width: 12,
    },
    availabilityGridItem: {
        width: 12,
    },
    attributesGridItem: {
        width: 12,
    },
    productSearchGridItem: {
        width: 12,
    },
    variantPropsGridItem: {
        width: 12,
    },
    variantLabelText: {
        weight: "normal",
        size: 14,
        css: css`
            &::after {
                content: ": ";
            }
        `,
    },
    minPackQuantityLabelText: {
        css: css`
            font-size: 14px;
            padding-right: 4px;
            &::after {
                content: ":";
            }
        `,
    },
    minPackQuantityValueText: {
        css: css`
            font-size: 14px;
        `,
    },
    textBadgeStyles: {
        badge: {
            css: css`
                margin: 5px 0;
            `,
        },
        badgeText: {
            css: css`
                padding: 0 10px;
            `,
        },
    },
    imageBadgeStyles: {
        badgeImageProps: {
            style: {
                width: "auto",
                maxWidth: "100%",
                maxHeight: "100%",
            },
        },
        imageItem: {
            css: css`
                padding: 5px;
                max-height: 50px;
                justify-content: center;
            `,
        },
    },
};

// <XNG-Changes>
const styles = mergeToNew(productInformationStyles, customProductInformationStyles);
// </XNG-Changes>

const ProductListProductInformation = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    isGridView,
    showVariantProps,
    showMinPack,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
}: Props) => {
    const { product, productInfo } = productContext as any;
    const minPackQuantityProduct = Number(productInfo?.pricing?.additionalResults.minPackQuantity);

    if (!product) {
        return null;
    }

    let variantsProps: any;
    if (product.properties.styledChildrenTraits) {
        variantsProps = JSON.parse(product.properties.styledChildrenTraits);
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    let variantDisplayTrait = null;
    if (product.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a: any, b: any) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find((x: any) => swatchedViewList.includes(x.displayType));
    }

    return (
        <GridContainer {...styles.container}>
            {product.brand && showBrand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            {showTitle && (
                <GridItem {...styles.descriptionGridItem}>
                    <ProductDescription product={productContext} extendedStyles={styles.descriptionStyles} />
                </GridItem>
            )}
            {showPartNumbers && (
                <GridItem {...styles.partNumbersGridItem}>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber || product.customerName}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.partNumbersStyles}
                    />
                </GridItem>
            )}
            {showMinPack && !!minPackQuantityProduct && minPackQuantityProduct !== 9999 && (
                <GridItem {...styles.minPackQuantityGridItem}>
                    <Typography {...styles.minPackQuantityLabelText}>{siteMessage("Rugby_LiftQty")}</Typography>
                    <Typography {...styles.minPackQuantityValueText}>
                        {minPackQuantityProduct.toLocaleString()}
                    </Typography>
                </GridItem>
            )}
            {/* <XNG-Changes> display variant child properties */}
            {showVariantProps &&
                variantsProps &&
                Object.keys(variantsProps)
                    .sort()
                    .map((key: any, index: number) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <GridItem {...styles.variantPropsGridItem} {...{ isGridView }} key={index}>
                            <StyledWrapper {...styles.variantWrapper}>
                                <Typography {...styles.variantLabelText}>{translate(key)}</Typography>
                                <Typography {...styles.variantValueText}>{variantsProps[key]}</Typography>
                            </StyledWrapper>
                        </GridItem>
                    ))}
            {/* </XNG-Changes> */}
            {showAvailability && (
                <GridItem {...styles.availabilityGridItem}>
                    <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                </GridItem>
            )}
            {variantDisplayTrait && (
                <GridItem {...styles.swatchGridItem}>
                    <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                </GridItem>
            )}
            {showTextBadges && textBadges && (
                <GridItem {...styles.textBadgeGridItem}>
                    <ProductTextBadges
                        badgeList={textBadges}
                        maxBadges={maxTextBadges ?? 4}
                        extendedStyles={styles.textBadgeStyles}
                    />{" "}
                </GridItem>
            )}
            {showImageBadges && imageBadges && (
                <GridItem {...styles.imageBadgeGridItem}>
                    <ProductImageBadges
                        badgeList={imageBadges}
                        maxBadges={maxImageBadges ?? 4}
                        extendedStyles={styles.imageBadgeStyles}
                    />
                </GridItem>
            )}
            {/* <XNG-Changes> pass isGridView to styledComponent */}
            {showAttributes && (
                <GridItem {...styles.attributesGridItem} {...{ isGridView }}>
                    <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributes} />
                </GridItem>
            )}
            {/* </XNG-Changes> */}

            {/* <XNG-Changes> restore Search Score functionality */}
            <GridItem {...styles.productSearchGridItem}>
                <ProductSearchScoreData />
            </GridItem>
            {/* </XNG-Changes> */}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductInformation));
